import React from 'react'
import Table from 'shared/components/Table'
import { customRelativeDateTimeFormat, customFullDateTimeFormat } from 'libs/date-format'
import { ListItemGroup } from 'components/ListItemGroup'
import { Dropdown, Popup as SemanticPopup, Segment, Button, Label } from 'semantic-ui-react'
import { getAntdColumnMaxWidth } from 'libs/table/utils'
import Popup from 'shared/components/Popup'
import { fieldIcons } from 'shared/constants/fieldIcons'

interface ApiKeyData {
  id: number
  name: string
  key_prefix: string
  scopes: string[]
  created: string
  created_by: string
  created_by_fallback: string
}

interface ApiKeyDataProps {
  dataSource: ApiKeyData[]
  handleOpenDeleteModal: (apiKeyId: number, apiKeyName: string) => void
  handleOpenAddEditModal: () => void
  handleOpenEditModal: (apiKeyId: number) => void
}

function getScopeDisplayData(scopes: string[]) {
  const formatAccess = (resource: string) => {
    const canRead = scopes.includes(`${resource}:read`)
    const canWrite = scopes.includes(`${resource}:write`)

    if (canRead && canWrite) {
      return 'Read and write'
    } else if (canRead) {
      return 'Read only'
    } else if (canWrite) {
      return 'Write only'
    } else {
      return 'None'
    }
  }

  const content = (
    <ListItemGroup
      title=""
      items={[
        { name: 'Answers', value: formatAccess('answers'), icon: fieldIcons.answers },
        {
          name: 'Customers',
          value: formatAccess('customers'),
          icon: fieldIcons.customer,
        },
        {
          name: 'Team members',
          value: formatAccess('team-members'),
          icon: fieldIcons.teamMember,
        },
        {
          name: 'Responses',
          value: formatAccess('responses'),
          icon: fieldIcons.responses,
        },
        {
          name: 'Surveys',
          value: formatAccess('surveys'),
          icon: fieldIcons.survey,
        },
        {
          name: 'Questions',
          value: formatAccess('questions'),
          icon: fieldIcons.questions,
        },
      ]}
    />
  )

  return content
}

const ApiKeyTable: React.FC<ApiKeyDataProps> = ({
  dataSource,
  handleOpenDeleteModal,
  handleOpenAddEditModal,
  handleOpenEditModal,
}) => {
  const columns = [
    {
      title: 'Key name',
      dataIndex: 'name',
      key: 'name',
      ...getAntdColumnMaxWidth(150),
      render(_, data) {
        return <Popup.Truncated content={data.name} />
      },
      sorter(dataA, dataB) {
        const labelA = dataA.name
        const labelB = dataB.name
        return (labelA || '').localeCompare(labelB)
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'API key',
      dataIndex: 'key_prefix',
      key: 'key_prefix',
      width: 220,
      render(_, data) {
        const keyPrefix = data.key_prefix
        const splitApiKey = keyPrefix.split('_')
        const [prefix, companyId, rest] = splitApiKey
        const truncatedApiKey = `${prefix}_${companyId}_${rest.slice(0, 8)}...`

        return <Label>{truncatedApiKey}</Label>
      },
    },
    {
      title: 'Created date',
      dataIndex: 'created',
      key: 'created',
      ...getAntdColumnMaxWidth(100),
      render(_, data) {
        return (
          <SemanticPopup
            basic
            size="mini"
            inverted
            trigger={<div className="truncate">{customRelativeDateTimeFormat(data.created)}</div>}
            content={customFullDateTimeFormat(data.created)}
          />
        )
      },
      sorter: (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime(),
      sortDirections: ['ascend', 'descend'],
      defaultSortOrder: 'descend',
    },
    {
      title: 'Created by',
      dataIndex: 'created_by',
      key: 'created_by',
      ...getAntdColumnMaxWidth(150),
      render(_, data) {
        const isRemoved = !data.created_by && data.created_by_fallback
        const createdByName = data.created_by ?? data.created_by_fallback ?? ''

        if (!isRemoved) {
          return <Popup.Truncated content={createdByName} />
        }

        // A user is removed by GDPR
        const isAnonymous = data.created_by_fallback?.toLowerCase() === 'anonymous'
        if (isAnonymous) {
          return <span>{createdByName}</span>
        }

        return (
          <SemanticPopup
            basic
            size="mini"
            inverted
            content={`${data.created_by_fallback} was removed from this workspace.`}
            trigger={<div className="truncate">{createdByName}</div>}
          />
        )
      },
      sorter(dataA, dataB) {
        const labelA = dataA.created_by ?? dataA.created_by_fallback
        const labelB = dataB.created_by ?? dataB.created_by_fallback

        return (labelA || '').localeCompare(labelB)
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Permissions',
      dataIndex: 'scopes',
      key: 'scopes',
      ...getAntdColumnMaxWidth(120),
      ellipsis: true,
      render(_, data) {
        if (data.is_full_access) {
          return <span>Full access</span>
        } else {
          const scopeDataContent = getScopeDisplayData(data.scopes)

          return (
            <SemanticPopup
              basic
              wide
              position="right center"
              trigger={<span>Custom</span>}
              content={
                <React.Fragment>
                  <h3 className="ui dividing header !pt-1 !pb-2.5">Custom access scopes</h3>
                  {scopeDataContent}
                </React.Fragment>
              }
            />
          )
        }
      },
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      width: 50,
      render(_, data) {
        return (
          <Dropdown
            icon="far fa-ellipsis-vertical"
            floating
            pointing="top right"
            className="table-elipsis-toolbar-dropdown"
          >
            <Dropdown.Menu>
              <Dropdown.Item text="Edit" icon="far fa-edit" onClick={() => handleOpenEditModal(data.id)} />
              <Dropdown.Item
                className="!text-negative"
                text="Delete"
                icon="far fa-trash-can"
                onClick={() => handleOpenDeleteModal(data.id, data.name)}
              />
            </Dropdown.Menu>
          </Dropdown>
        )
      },
    },
  ]

  if (dataSource && dataSource.length < 1) {
    return <EmptyApiKeyTable handleOpenAddEditModal={handleOpenAddEditModal} />
  }

  return (
    <div className="ui">
      <Table
        variant="indexPage"
        clickable={false}
        dataSource={dataSource}
        columns={columns}
        pagination={false}
      />
    </div>
  )
}

const EmptyApiKeyTable = ({ handleOpenAddEditModal }) => (
  <Segment padded="very" textAlign="center">
    <h3 className="text-[color:var(--grey)]">No API keys yet</h3>
    <p className="subtext">Create an API key to get started.</p>
    <Button primary onClick={handleOpenAddEditModal}>
      <i className="icon far fa-plus" />
      New API key
    </Button>
  </Segment>
)

export default ApiKeyTable



// WEBPACK FOOTER //
// ./src/pages/Admin/ApiKey/ApiKeyTable.tsx